<template>
  <div
    role="alertdialog"
    class="m-popover-notification"
    :class="{
      [`m-popover-notification--${alertType}`]: alertType,
    }"
    :aria-label="`${alertType} message`"
    :aria-describedby="id"
  >
    <div class="m-popover-notification__icon">
      <MIcon block :icon="icon" size="lg" />
    </div>
    <div :id="id" class="m-popover-notification__content">
      <slot />
    </div>
    <div class="m-popover-notification__close">
      <MButtonIcon
        size="lg"
        icon="x"
        :aria-label="closeLabel"
        @click="$emit('close')"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, useId } from 'vue'
import { useI18n } from 'vue-i18n'

import { alertIcon } from '../../../lib/alert-icon'
import { AlertType } from '../../../types/types'
import MButtonIcon from '../../controls/ButtonIcon/MButtonIcon.vue'
import MIcon from '../../typography/Icon/MIcon.vue'

import gb from './locales/en-GB.json'
import us from './locales/en-US.json'
import sv from './locales/sv.json'

/**
 * Non-blocking alerts to give the user feedback without getting in their way.
 *
 * These are particularly useful for cross route feedback such as submitting a form and getting confirmation of success in the next view.
 *
 * A maximum of 6 messages should be displayed at one time.
 */
export default defineComponent({
  name: 'MPopoverNotification',
  components: {
    MIcon,
    MButtonIcon,
  },
  props: {
    alertType: {
      type: String as PropType<AlertType>,
      default: 'info',
    },
  },
  emits: ['close'],
  setup(props) {
    const { t } = useI18n()
    const id = useId()

    const closeLabel = computed(() => t('MPopoverNotification.dismiss'))

    const icon = alertIcon(props.alertType)

    return {
      closeLabel,
      icon,
      id,
    }
  },
  i18n: {
    messages: {
      'en-GB': gb,
      'en-US': us,
      sv: sv,
    },
  },
})
</script>

<style lang="scss" scoped>
$types: 'info', 'success', 'warning', 'error';

.m-popover-notification {
  background: var(--skin-background-light);
  border-left: 4px solid;
  box-shadow:
    0 6px 6px 0 rgba(10, 22, 70, 0.1),
    0 0 1px 0 rgba(10, 22, 70, 0.1);
  color: var(--skin-color-light);
  display: flex;
  gap: var(--spacing-12);
  padding: var(--spacing-16) var(--spacing-16);
  width: 100%;

  &__icon {
    align-items: center;
    display: flex;
    height: calc(var(--typography-paragraph-regular-line-height) * 1em);
  }

  &__content {
    flex: 1 1 auto;
  }

  &__close {
    margin: -5px 0;
  }

  @each $type in $types {
    &--#{$type} {
      border-color: var(--state-#{$type}-dark);
      .m-popover-notification__icon {
        color: var(--state-#{$type}-dark);
      }
    }
  }
}
</style>
